import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useStore } from 'components/Store';
import ListView from 'components/RecordsListView/ListView';
import { setPrintListenerReady } from 'components/Notification/genericNotification';
import useInvoiceActions from 'screens/Invoices/hooks/useInvoiceActions';
import SortDropdownMenu, {
  SortOption
} from 'components/RecordsListView/SortDropdownMenu';
import { FILTER_LABEL } from 'screens/Applications&WorkOrders/Components/Constants';
import {
  useSearch,
  usePagination,
  useSort,
  useSummaryParams
} from 'components/RecordsListView/context/SummaryParamsContext';
import useListViewSelection from 'hooks/useListViewSelection';
import useDeepLinkSelection from 'hooks/useDeepLinkSelection';
import useInvoiceListViewColumns from '../hooks/useInvoiceListViewColumns';
import useInvoiceEvents from '../hooks/useInvoiceEvents';
import { registerInvoicePrinter } from './Notification/invoicePrintBridge';
import { InvoiceActionMenuInvoice } from './InvoiceActionMenu';
import InvoiceDetailPanel from './InvoiceDetailPanel';
import InvoiceFilterDrawer from './InvoiceFilterDrawer';
import { InvoiceFilters, InvoiceSummary } from '../types';

export interface InvoiceListViewConfig {
  icon: React.ReactNode;
  title: string;
  searchPlaceholder: string;
  createButtonLabel: string;
  /** Base invoices path; create navigates to `${createPath}/create`. */
  createPath: string;
  sortOptions?: SortOption[];
}

export interface InvoiceListViewProps {
  invoices: InvoiceSummary[];
  totalElements: number;
  currentPage: number;
  pageSize: number;
  isLoading?: boolean;
  onItemDeleted?: (itemId: string) => void;
  config: InvoiceListViewConfig;
}

const CREATE_ROLES = new Set(['full control', 'admin']);

/**
 * Resolves the member's effective financial access, replicating the legacy
 * invoices rule: Full control / View Only members without an explicit
 * `financialAccess` field are treated as `none` (cost masked).
 */
const resolveFinancialAccess = (perm?: {
  role?: string;
  financialAccess?: string;
}): string | undefined => {
  if (!perm) return undefined;
  const masked =
    (perm.role === 'Full control' || perm.role === 'View Only') &&
    !Object.hasOwn(perm, 'financialAccess');
  return masked ? 'none' : perm.financialAccess;
};

/**
 * Smart container for the redesigned invoices landing. Feeds the shared
 * `<ListView>` with the invoice rows, columns/selection, sort dropdown, search,
 * and pagination. The detail panel (US2) and invoice filter drawer (US3) are
 * wired in later phases — only the drawer toggle is stubbed here.
 */
const InvoiceListView: React.FC<InvoiceListViewProps> = ({
  invoices,
  totalElements,
  currentPage,
  pageSize,
  isLoading = false,
  onItemDeleted,
  config
}) => {
  const history = useHistory();
  const {
    state: { loggedInUserOrgPermission }
  } = useStore();
  const {
    sendAccessEvent,
    sendInitiateCreateEvent,
    sendViewInvoiceEvent
  } = useInvoiceEvents();

  const memberRole: string | undefined = loggedInUserOrgPermission?.role;
  const memberFinancialAccess = resolveFinancialAccess(
    loggedInUserOrgPermission
  );
  const canCreate = CREATE_ROLES.has(memberRole?.toLowerCase() ?? '');

  const { searchTerm, handleSearchChange } = useSearch();
  const { config: paginationConfig } = usePagination({
    currentPage,
    totalElements,
    pageSize
  });
  useSort();

  const {
    hasFilters,
    isDrawerVisible,
    setIsDrawerVisible,
    handleFiltersChange,
    filters
  } = useSummaryParams();

  const invoiceFilters: InvoiceFilters = filters;

  const dataSource = invoices.map(invoice => ({
    ...invoice,
    id: invoice.invoiceId
  }));

  // Pre-select the invoice specified by a deep-link redirect (FR-023).
  const deepLinkInvoiceId = useDeepLinkSelection('selectedInvoiceId');
  const { selectedId, handleSelect, handleItemDeleted } = useListViewSelection({
    items: dataSource,
    isLoading,
    preferredId: deepLinkInvoiceId
  });

  useEffect(() => {
    sendAccessEvent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const invoiceActions = (useInvoiceActions() as unknown) as {
    handleAction: (action: string, invoice: InvoiceActionMenuInvoice) => void;
  };
  const handleActionRef = useRef(invoiceActions.handleAction);
  handleActionRef.current = invoiceActions.handleAction;
  useEffect(() => {
    setPrintListenerReady(true);
    registerInvoicePrinter(invoice =>
      handleActionRef.current('downloadPDFEnglish', invoice)
    );
    return () => {
      setPrintListenerReady(false);
      registerInvoicePrinter(null);
    };
  }, []);

  // The shared DetailPanel has no open/close state; track an explicit dismiss
  // (Escape) so the panel can be "closed" without losing the row selection.
  const [isPanelDismissed, setIsPanelDismissed] = useState(false);

  const handleRowSelect = (id: string | null) => {
    setIsPanelDismissed(false);
    handleSelect(id);
  };

  const handleInvoiceDeleted = (deletedId: string) => {
    handleItemDeleted(deletedId, onItemDeleted);
  };

  const { rowSelection, columns } = useInvoiceListViewColumns({
    selectedId,
    onSelectId: handleRowSelect,
    searchTerm,
    memberRole,
    memberFinancialAccess
  });

  const selectedInvoice = invoices.find(
    invoice => invoice.invoiceId === selectedId
  );

  // Per FR-050 the list's no-results empty state always reads exactly this,
  // regardless of whether the empty result is due to search or filters.
  const emptyMessage = 'No Invoices found.';

  const handleCreateClick = () => {
    sendInitiateCreateEvent();
    history.push(`${config.createPath}/create`);
  };

  const handleFilterClick = () => {
    setIsDrawerVisible(true);
  };

  return (
    <ListView
      icon={config.icon}
      title={config.title}
      searchPlaceholder={config.searchPlaceholder}
      searchText={searchTerm}
      onSearchChange={handleSearchChange}
      sortDropdown={
        <SortDropdownMenu
          sortOptions={config.sortOptions}
          defaultLabel="Invoice Date"
        />
      }
      createButtonLabel={config.createButtonLabel}
      onCreateClick={handleCreateClick}
      canCreate={canCreate}
      filterButtonLabel={FILTER_LABEL}
      onFilterClick={handleFilterClick}
      hasActiveFilters={hasFilters}
      dataSource={dataSource}
      columns={columns}
      rowKey="id"
      rowSelection={rowSelection}
      pagination={paginationConfig}
      onRowClick={record => {
        sendViewInvoiceEvent();
        handleRowSelect(record.id);
      }}
      isLoading={isLoading}
      emptyMessage={emptyMessage}
      detailPanel={
        <InvoiceDetailPanel
          operationId={isPanelDismissed ? null : selectedId}
          invoiceSummary={selectedInvoice}
          hasInvoices={dataSource.length > 0}
          isLoading={isLoading}
          memberRole={memberRole}
          memberFinancialAccess={memberFinancialAccess}
          canModify={canCreate}
          onInvoiceDeleted={handleInvoiceDeleted}
          onClose={() => setIsPanelDismissed(true)}
        />
      }
      filterDrawer={
        <InvoiceFilterDrawer
          visible={isDrawerVisible}
          initialValues={invoiceFilters}
          onClose={() => setIsDrawerVisible(false)}
          onFiltersChange={handleFiltersChange}
        />
      }
    />
  );
};

export default InvoiceListView;
